import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/snackbarSlice';

function ShareDashboardModal({ open, onClose, dashboardId }) {
  const dispatch = useDispatch();
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inviterEmail = JSON.parse(localStorage.getItem('user'))?.user?.email || '';

  const resetAndClose = () => {
    setInviteeEmail('');
    setIsSubmitting(false);
    onClose && onClose();
  };

  const handleSubmit = async () => {
    const email = inviteeEmail.trim();
    if (!email || !email.includes('@')) {
      dispatch(showSnackbar({ message: 'Please enter a valid email.', severity: 'error' }));
      return;
    }
    if (!dashboardId) {
      dispatch(showSnackbar({ message: 'Missing dashboard id.', severity: 'error' }));
      return;
    }
    if (!inviterEmail) {
      dispatch(showSnackbar({ message: 'Missing inviter email. Please sign in.', severity: 'error' }));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        dashboard_id: String(dashboardId),
        inviter_email: inviterEmail,
        invitee_email: email,
      };

      // console.log('POST /dashboard/share payload:', payload);

      const res = await fetch('https://api.vesterai.com/dashboard/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      // console.log('POST /dashboard/share response status:', res.status);
      // console.log('POST /dashboard/share response body:', data);

      if (!res.ok) {
        throw new Error(data?.message || `Request failed: ${res.status}`);
      }

      dispatch(showSnackbar({ message: 'Invite sent successfully.', severity: 'success' }));
      resetAndClose();
    } catch (err) {
      console.error('Error sharing dashboard:', err);
      dispatch(showSnackbar({ message: 'Failed to send invite. Please try again.', severity: 'error' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={resetAndClose}
      maxWidth="sm" 
      fullWidth
      aria-labelledby="share-dashboard-dialog-title"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          background: 'rgb(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '2px solid rgba(200, 200, 200, 0.3)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <DialogTitle id="share-dashboard-dialog-title" sx={{ marginTop: '10px', marginLeft: '3px', padding: '10px 24px' }}>Share Dashboard</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          value={inviteeEmail}
          onChange={(e) => setInviteeEmail(e.target.value)}
          variant="outlined"
          aria-label="Invitee email"
          placeholder="Enter email to share with"
          type="email"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              '& fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&.Mui-focused': {
                boxShadow: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
              },
            },
            '& input': {
              color: '#333',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ marginBottom: '10px', marginRight: '17px' }}>
        <Button 
          onClick={resetAndClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(150, 150, 150, 0.3)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.6)',
              borderColor: 'rgba(150, 150, 150, 0.4)',
              boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(18, 157, 232, 0.1)',
            boxShadow: 'none',
            border: '1px solid rgba(18, 157, 232, 0.2)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '140px',
            '&:hover': {
              background: 'rgba(18, 157, 232, 0.2)',
              borderColor: 'rgba(18, 157, 232, 0.5)',
              boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShareDashboardModal;


